import {
    Accordion,
    AccordionItem,
    AccordionTrigger,
    AccordionContent,
} from "@/components/ui/accordion";
import { ItemHeader } from "./ItemHeader";
import { AddGroupRow } from "./AddGroupRow";
import { GroupAccordion } from "./GroupAccordion";
import { Button } from "@/components/ui/button";
import { Settings, Trash } from "lucide-react";
import { useState } from "react";
import { cn } from "@client/lib";
import { ConditionalBuilder } from "./ConditionalBuilder";

interface SubsectionAccordionProps {
    sectionId: string;
    subsection: PolicyFormSubSection;
    questions: Record<string, PolicyFormQuestion>;
    onSubsectionChange: (
        sectionId: string,
        subsectionId: string,
        updates: Partial<PolicyFormSubSection>
    ) => void;
    onQuestionGroupChange: (
        sectionId: string,
        subsectionId: string,
        groupId: string,
        updates: Partial<PolicyFormQuestionGroup>
    ) => void;
    onQuestionChange: (questionId: string, updates: Partial<PolicyFormQuestion>) => void;
    onRemoveQuestion: (
        sectionId: string,
        subsectionId: string,
        groupId: string,
        questionId: string
    ) => void;
    onAddGroup: (sectionId: string, subsectionId: string, group: PolicyFormQuestionGroup) => void;
    onAddQuestionToGroup: (
        sectionId: string,
        subsectionId: string,
        groupId: string,
        question: PolicyFormQuestion
    ) => void;
    onToggleConditional?: () => void;
    onRemoveSubsection: (
        sectionId: string,
        subsectionId: string
    ) => void;
    onRemoveGroup: (
        sectionId: string,
        subsectionId: string,
        groupId: string
    ) => void;
}

export function SubsectionAccordion({ sectionId, subsection, onRemoveSubsection, onRemoveGroup, ...props }: SubsectionAccordionProps) {
    const [isConditionOpen, setIsConditionOpen] = useState(false);

    return (
        <Accordion type="multiple" className="space-y-2">
            <AccordionItem value={subsection.id} className="border rounded-md bg-white">
                <AccordionTrigger className="px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-md text-gray-900">
                    <ItemHeader
                        title={subsection.title}
                        onTitleChange={(t) =>
                            props.onSubsectionChange(sectionId, subsection.id, { title: t })
                        }
                        onToggleConditional={props.onToggleConditional}
                    />
                    <button
                        type="button"
                        aria-label="Edit conditional logic"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsConditionOpen(true);
                        }}
                    >
                        <Settings className={cn("h-4 w-4 flex-shrink-0", subsection.conditional ? "text-green-500" : "text-gray-500")} />
                    </button>

                    <ConditionalBuilder
                        open={isConditionOpen}
                        title={`Conditional Logic - Subsection`}
                        visibleCondition={subsection.conditional?.visible}
                        requiredCondition={subsection.conditional?.required}
                        onChangeVisible={(c) =>
                            props.onSubsectionChange(sectionId, subsection.id, { conditional: { ...subsection.conditional, visible: c } })
                        }
                        onChangeRequired={(c) =>
                            props.onSubsectionChange(sectionId, subsection.id, { conditional: { ...subsection.conditional, required: c } })
                        }
                        onClose={() => {
                            setIsConditionOpen(false);
                        }}
                        questions={props.questions}
                    />

                    <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Remove subsection"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onRemoveSubsection(sectionId, subsection.id);
                        }}
                    >
                        <Trash size={14} className="text-red-500" />
                    </Button>
                </AccordionTrigger>

                <AccordionContent className="px-3 py-2 space-y-3">
                    {subsection.questionGroups.map((group) => (
                        <GroupAccordion
                            key={group.id}
                            sectionId={sectionId}
                            subsectionId={subsection.id}
                            group={group}
                            onRemoveGroup={onRemoveGroup}
                            {...props}
                        />
                    ))}
                    <AddGroupRow
                        sectionId={sectionId}
                        subsectionId={subsection.id}
                        onAddGroup={props.onAddGroup}
                    />
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}